'use client';

import { useTranslations } from 'next-intl';

import { useProductAttributes } from '@/modules/attributes/useProductAttributes';
import { useBrands } from '@/modules/brands/hooks/useBrands';
import { useCategoryFilters } from '@/modules/categories/useCategoryFilters.ts';
import { getCurrencySymbolFromCountry } from '@/modules/currencies/helpers';
import { getDepartmentId } from '@/modules/drafts/helpers/categories.ts';
import { decodeCompositeSizeId } from '@/modules/filters/helpers';
import { type ActiveFilter } from '@/modules/filters/types';
import { useSizeFilters } from '@/modules/filters/useSizeFilters';
import { useCurrentLocation } from '@/modules/location/useCurrentLocation';
import { useSearchFilters } from '@/modules/search/useSearchFilters';

export function useBuildActiveFilters(): ActiveFilter[] {
  const {
    searchFilters: {
      brands,
      colours,
      conditions,
      isDiscounted,
      priceMin,
      priceMax,
      sizes,
      groups,
      productTypes,
      isKids,
      gender,
    },
    setSearchFilters: {
      setBrandFilter,
      setGroupsFilter,
      setColourFilter,
      setConditionFilter,
      setIsDiscountedFilter,
      setPriceFilter,
      setSizeFilter,
      setProductTypeFilter,
    },
  } = useSearchFilters();

  const { data: brandData } = useBrands();
  const { data: attributesData } = useProductAttributes();
  const { data: categoryData } = useCategoryFilters();
  const { data: sizeData } = useSizeFilters();
  const { location } = useCurrentLocation();
  const currencySymbol = getCurrencySymbolFromCountry(location);
  const searchT = useTranslations('search');

  function buildActiveCategoryFilter() {
    if (!groups || !categoryData) {
      return [];
    }

    let activeCategoryName = '';
    let parentCategoryName = '';
    const department = getDepartmentId(gender, isKids);

    const activeDepartment = categoryData?.data?.departments?.find(
      (parent) => parent.id === department
    );
    const activeCategory = activeDepartment?.children.find(
      (child) => child.id === groups
    );

    activeCategoryName = activeCategory?.name || '';
    parentCategoryName = activeDepartment?.name || '';

    return [
      {
        name: `${parentCategoryName} / ${activeCategoryName}`,
        handleClick: () => setGroupsFilter(),
      },
    ];
  }

  function buildActiveSubcategoryFilters() {
    if (!productTypes || productTypes.length === 0 || !categoryData) {
      return [];
    }

    return productTypes.map((productType) => {
      const activeSubcategory =
        groups &&
        categoryData?.data.groups[groups]?.find((subcategory) => {
          return subcategory.id === productType;
        });

      const subcategoryName = activeSubcategory ? activeSubcategory?.name : '';

      return {
        name: subcategoryName,
        handleClick: () => setProductTypeFilter(productType),
      };
    });
  }

  function buildActiveBrandFilters() {
    if (!brands || brands.length === 0 || !brandData) {
      return [];
    }

    return brands.map((brandId) => ({
      name: brandData.data[Number(brandId)]?.name || '',
      handleClick: () => setBrandFilter(brandId),
    }));
  }

  function buildSizeName(compositeSizeId: string) {
    const { variantSetId, variantId } = decodeCompositeSizeId(compositeSizeId);

    // Walk the size tree: department → sizeSet label → locale (variantSetId) → sizes (variantId)
    let department;
    let sizeSetLabel;
    let size;

    for (const topLevelNode of sizeData?.data ?? []) {
      for (const sizeSetNode of topLevelNode.children) {
        const localeNode = sizeSetNode.children.find(
          (localeToCheck) => localeToCheck.id === variantSetId
        );
        if (localeNode) {
          const matchedSize = localeNode.children.find(
            (sizeToCheck) => sizeToCheck.id === variantId
          );
          if (matchedSize) {
            department = topLevelNode;
            sizeSetLabel = sizeSetNode;
            size = matchedSize;
            break;
          }
        }
      }
      if (size) {
        break;
      }
    }

    if (!groups) {
      return `${department?.name} ${sizeSetLabel?.name}: ${size?.name}`;
    }

    if (gender || isKids) {
      return `${sizeSetLabel?.name}: ${size?.name}`;
    }

    return size?.name || '';
  }

  function buildActiveSizeFilters() {
    if (!sizes || sizes.length === 0 || !sizeData) {
      return [];
    }

    return sizes.map((compositeSizeId) => {
      return {
        name: buildSizeName(compositeSizeId),
        handleClick: () => setSizeFilter(compositeSizeId),
      };
    });
  }

  function buildActivePriceFilter() {
    if (!priceMin && !priceMax) {
      return [];
    }

    let name = '';

    if (priceMin && priceMax) {
      name = `${currencySymbol}${priceMin} - ${currencySymbol}${priceMax}`;
    } else if (priceMin) {
      name = `${searchT('MoreThan')} ${currencySymbol}${priceMin}`;
    } else {
      name = `${searchT('LessThan')} ${currencySymbol}${priceMax}`;
    }

    return [{ name, handleClick: () => setPriceFilter({}) }];
  }

  function buildActiveColourFilters() {
    if (!colours || colours.length === 0 || !attributesData) {
      return [];
    }

    return colours.map((colourKey) => ({
      name:
        attributesData?.colour?.find((colour) => colour.id === colourKey)
          ?.nameI18N || '',
      handleClick: () => setColourFilter(colourKey),
    }));
  }

  function buildActiveConditionFilters() {
    if (!conditions || conditions.length === 0 || !attributesData) {
      return [];
    }

    return conditions.map((conditionKey) => ({
      name:
        attributesData?.condition?.find(
          (condition) => condition.id === conditionKey
        )?.nameI18N || '',
      handleClick: () => setConditionFilter(conditionKey),
    }));
  }

  function buildActiveOnSaleFilter() {
    if (isDiscounted) {
      return [
        {
          name: searchT('OnSale'),
          handleClick: () => setIsDiscountedFilter(false),
        },
      ];
    }

    return [];
  }

  const activeFilters = [
    ...buildActiveCategoryFilter(),
    ...buildActiveSubcategoryFilters(),
    ...buildActiveBrandFilters(),
    ...buildActiveSizeFilters(),
    ...buildActivePriceFilter(),
    ...buildActiveColourFilters(),
    ...buildActiveConditionFilters(),
    ...buildActiveOnSaleFilter(),
  ];

  return activeFilters;
}
